<template>
  <div :class="{'map-container': true, 'map-container--disable-zoom': !zooming}">
    <div
      ref="mapElement"
      class="map-container__map"
    />
    <Overlay
      :overlays="overlays"
      @change="clearOverlays"
      @load="loadOverlay"
    />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import 'leaflet/dist/leaflet.css';
import 'leaflet-editable';
import {
  Map,
  Polygon,
  Marker,
  TileLayer,
  LatLng,
  LatLngBounds,
  Polyline,
  CRS,
} from 'leaflet';

import {
  POLYGON_OPTIONS,
  DEFAULT_POINT_ZOOM,
  ARCGIS_BASE_URL,
  MAXIMUM_MARKERS,
  MINIMUM_MARKER_DISTANCE_METERS,
  CMP_BY_ZINDEX,
} from '@/vendor/leaflet_mods';
import binaryInsert from '@/functions/binary_insert';
import { TILES_BASE_URI } from '@/config/constants';

import { leafletPolygonToLatLngs, rgeoToPolygon } from '@/functions/geospatial_conversions';
import MapCrosshairLayer from '@/vendor/leaflet_layers/map_crosshair_layer';

import Overlay from '@/components/mapping/overlay.vue';

const DEFAULT_CENTER = [-26.145918, 28.043321]; // Rosebank Mall

const props = defineProps({
  center: { type: Array, default: null },
  editable: Boolean,
  defaultPolygonOptions: { type: Object, default: () => POLYGON_OPTIONS },
  overlays: { type: Array, default: () => [] },
  operationBounds: { type: String, default: null },
  diagram: { type: String, default: null },
  addDiagramBounds: { type: Boolean, default: false },
  dragging: { type: Boolean, default: true },
  zooming: { type: Boolean, default: true },
});
const emit = defineEmits([
  'init',
  'drag',
  'polygonUpdated',
  'polygonComplete',
  'selectOperationBounds',
  'mapClick',
  'markerClick',
  'polygonClick',
  'bounds'
]);

/**
 * @var
 * @type {L.Map}
 */
let map;
const mapElement = ref(null);
const resetZoomData = ref({});

let markers = [];
let visibleMarkers = [];
let mapOverlays = [];

let operationBounds;
let polygonCreated = false;
let defaultPolygonOptions = { ...POLYGON_OPTIONS, ...props.defaultPolygonOptions };

let crosshair;
let vline = null;
let hline = null;

let diagramDimensions = null;

onMounted(async () => {
  await initMap();
  await initOperationBounds();
  emit('init');
});

onBeforeUnmount(() => {
  map.off();
  map.remove();
});

async function initMap() {
  let mapOptions = {
    maxBoundsViscosity: 0.8,
    worldCopyJump: !props.diagram,
  };
  if (props.editable) {
    mapOptions.editable = true;
    mapOptions.editOptions = {
      lineGuideOptions: {
        ...defaultPolygonOptions,
        weight: 1,
        dashArray: null,
        opacity: 0.69,
      },
    };
  }
  if (props.diagram) {
    mapOptions.crs = CRS.Simple;
  }

  // @ts-ignore
  map = new Map(mapElement.value, mapOptions);

  if (props.diagram) {
    await initDiagram();
  } else {
    initEsriMap();
    map.setView(props.center || DEFAULT_CENTER, 18);
    resetZoomData.value = { center: props.center || DEFAULT_CENTER, zoom: 18 };
  }
  addEvents();

  map.on('move', () => {
    let { lat, lng } = map.getCenter();
    emit('drag', [lat, lng]);
  });

  // Disables dragging of the map
  !props.dragging && map.dragging.disable();

  // Disables zooming of the map
  if (!props.zooming) {
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    map.scrollWheelZoom.disable();
    map.boxZoom.disable();
  }
  /**
   * keyboard functions causing initial point allocation issues
   * we don't need them...
   */
  map.keyboard.disable();
}

async function initDiagram() {
  let response = await fetch(`${TILES_BASE_URI}/diagrams/${props.diagram}/metadata.json`);
  if (response.ok) {
    let metadata = await response.json();
    new TileLayer(
      `${TILES_BASE_URI}/diagrams/${props.diagram}/tiles/{z}/{x}/{y}/tile.png`, {
      minZoom: metadata.min_zoom_level,
      maxZoom: metadata.max_zoom_level,
    }).addTo(map);
    map.minZoon = metadata.min_zoom_level;
    map.maxZoon = metadata.max_zoom_level;
    map.setMaxBounds([[0, 0], [-metadata.height, metadata.width]]);
    map.setView(props.center || [-metadata.height / 2, metadata.width / 2], metadata.min_zoom_level);
    resetZoomData.value = { center: props.center || [-metadata.height / 2, metadata.width / 2], zoom: metadata.min_zoom_level };
    diagramDimensions = [metadata.width, metadata.height];
    emit('bounds', diagramDimensions);
    setDiagramMaxBounds(diagramDimensions);
  }
}

async function setDiagramMaxBounds(dimensions) {
  if (props.addDiagramBounds) {
    // Add bounds for diagrams based on the dimensions
    let diagramBounds = null;
    if (dimensions) {
      diagramBounds = rgeoToPolygon(`POLYGON ((
        0 0,
        ${dimensions[0]} 0,
        ${dimensions[0]} -${dimensions[1]},
        0 -${dimensions[1]},
        0 0
      ))`);

      operationBounds = addPolygon(diagramBounds, {
        className: 'region-polygon region-polygon--operation'
      });
      await restrictToOperationBounds();
      onObjectClick(operationBounds, () => emit('selectOperationBounds'));
    }
  }
}

function diagramBoundsValidation(latLng) {
  if (!diagramDimensions || !latLng) return;

  const isValidLat = latLng[0] >= 0 && latLng[0] <= diagramDimensions[0];
  const isValidLng = latLng[1] <= 0 && latLng[1] >= -diagramDimensions[1];

  return !isValidLat || !isValidLng;
}

function resetZoom() {
  if (!Object.prototype.hasOwnProperty.call(resetZoomData.value, 'center') || !Object.prototype.hasOwnProperty.call(resetZoomData.value, 'zoom')) return;
  map.setView(resetZoomData.value.center, resetZoomData.value.zoom);
}

function initEsriMap() {
  new TileLayer(
    `${ARCGIS_BASE_URL}/{z}/{y}/{x}`, {
    maxZoom: 20,
  }).addTo(map);
}

function addEvents() {
  map.addEventListener('editable:created', event => {
    if (event.layer instanceof Polygon) {
      polygonCreated = true;
      event.layer.setStyle(defaultPolygonOptions);
    }
  });

  map.addEventListener('editable:drawing:commit', event => {
    if (polygonCreated && event.layer instanceof Polygon) {
      polygonCreated = false;
      let polygon = event.layer;
      emit('polygonComplete', polygon, leafletPolygonToLatLngs(polygon.getLatLngs()));
      onObjectClick(polygon, event => emit('polygonClick', polygon, event));
    }
  });

  /**
   * @param event {L.LeafletEvent}
   */
  let updateHandler = event => {
    if (!polygonCreated && event.layer instanceof Polygon) {
      let polygon = event.layer;
      let latLngs = polygon.getLatLngs();
      polygon.setLatLngs(latLngs);
      polygon.editor.reset();
      emit('polygonUpdated', polygon, leafletPolygonToLatLngs(latLngs));
    }
  };
  ['editable:vertex:deleted', 'editable:vertex:dragend'].forEach(eventName => {
    map.addEventListener(eventName, updateHandler);
  });

  map.addEventListener('click', (event) => emit('mapClick', event));
}

async function initOperationBounds() {
  if (props.operationBounds && !props.addDiagramBounds) {
    operationBounds = addPolygon(rgeoToPolygon(props.operationBounds), {
      className: 'region-polygon region-polygon--operation'
    });
    await restrictToOperationBounds();
    onObjectClick(operationBounds, () => emit('selectOperationBounds'));
  }
}

async function restrictToOperationBounds() {
  await restrictToBounds(operationBounds.getBounds(), { pad: 0.5 });
}

function isCloseTo(marker, otherMarker) {
  let distance = marker.getLatLng().distanceTo(otherMarker.getLatLng());
  return distance < MINIMUM_MARKER_DISTANCE_METERS;
}

function onObjectClick(object, cb) {
  object.on('click', (event) => {
    event.originalEvent.preventDefault();
    event.originalEvent.stopPropagation();
    event.originalEvent.stopImmediatePropagation();
    cb(event);
  });
}

/**
 * @param {L.LatLngExpression} latLng
 */
function setCenter(latLng) {
  map.setView(latLng, map.getZoom());
}

function getCenter() {
  return map.getCenter();
}

function addMarker(latLng = getCenter(), options = {}) {
  let marker = new Marker(latLng, options);
  onObjectClick(marker, event => emit('markerClick', marker, event));
  markers.push(marker);
  if (visibleMarkers.length < MAXIMUM_MARKERS) {
    let closeMarker = visibleMarkers.find(m => isCloseTo(m, marker));
    if (!closeMarker || closeMarker.zIndexOffset < marker.zIndexOffset) {
      showMarker(marker);
    }
  }
  return marker;
}

function showMarker(marker) {
  if (marker._map === map) return;
  if (visibleMarkers.length === MAXIMUM_MARKERS) {
    removeObject(visibleMarkers.shift());
  }
  let closeMarkerIndex = visibleMarkers.findIndex(m => isCloseTo(m, marker));
  if (closeMarkerIndex !== -1) {
    removeObject(visibleMarkers[closeMarkerIndex]);
    visibleMarkers.splice(closeMarkerIndex, 1);
  }
  marker.addTo(map);
  binaryInsert(visibleMarkers, marker, CMP_BY_ZINDEX);
}

function clearMarkers() {
  visibleMarkers.forEach(marker => removeObject(marker));
  markers = [];
  visibleMarkers = [];
}

/**
 * @param {L.Layer} object
 */
function removeObject(object) {
  map.removeLayer(object);
}

/**
 * Add a polygon to the map
 * @param {[number, number][][]} coordinates - [[[latitude, longitude], ...], ...]
 */
function addPolygon(coordinates, options = {}) {
  let polygon = new Polygon(coordinates, { ...defaultPolygonOptions, ...options });
  onObjectClick(polygon, event => emit('polygonClick', polygon, event));
  polygon.addTo(map);
  return polygon;
}

/**
 * Initiate polygon drawing mode
 */
function startPoly() {
  // @ts-ignore
  map.editTools.startPolygon();
}

function stopDrawing() {
  /**
   * @param {L.LeafletEvent} event
   */
  let onCancel = (event) => {
    map.removeLayer(event.layer);
    map.off('editable:drawing:cancel', onCancel);
  };
  map.on('editable:drawing:cancel', onCancel);
  // @ts-ignore
  map.editTools.stopDrawing();
}

/**
 * @returns {boolean}
 */
// @ts-ignore
function isDrawing() {
  return map.editTools.drawing();
}

async function refresh() {
  map.invalidateSize({ animate: false, duration: 0, noMoveStart: true });
  if (operationBounds) {
    await restrictToOperationBounds();
  }
}

/**
 * Adds a map tile overlay
 * @param {string} uriTemplate uri template for tiles, e.g. "www.example.com/{x}/{y}/{z}.png"
 * @param {object} param1 options { minZoom, maxZoom }
 */
function addOverlay(uriTemplate, { minZoom = 0, maxZoom = 20 }) {
  let overlay = new TileLayer(uriTemplate, { minZoom, maxZoom });
  overlay.addTo(map);
  mapOverlays.push(overlay);
  return overlay;
}

/**
 * Clears all additional map tile overlays
 */
function clearOverlays() {
  mapOverlays.forEach(overlay => map.removeLayer(overlay));
  mapOverlays = [];
}

function loadOverlay({ overlay, metadata }) {
  if (overlay) {
    addOverlay(`${overlay.base_uri}/tiles/{z}/{x}/{y}/tile.png`, {
      minZoom: metadata.min_zoom_level,
      maxZoom: metadata.max_zoom_level
    });
  }
}

function addPolyline(latLngs, options = {}) {
  return new Polyline(latLngs, options).addTo(map);
}

/**
 * Get the current viewport bounds of the map
 *
 * @returns {[number, number, number, number]} [lat1, lng1, lat2, lng2]
 */
function getBounds() {
  let bounds = map.getBounds();

  return [
    bounds.getSouth(),
    bounds.getWest(),
    bounds.getNorth(),
    bounds.getEast()
  ];
}

/**
 * Smoothly pans and zooms the view to fit the given bounds
 *
 * @param {number} south
 * @param {number} west
 * @param {number} north
 * @param {number} east
 */
function flyToBounds(south, west, north, east) {
  let corner1 = new LatLng(south, west),
    corner2 = new LatLng(north, east),
    bounds = new LatLngBounds(corner1, corner2);

  map.flyToBounds(bounds);
}

/**
 * Fly to the given position.
 *   If zoom is given, smoothly zooms to that level,
 *   otherwise just pans at the current zoom.
 *
 * @param {[number, number]} latLng [latitude, longitude]
 * @param {number} zoom optional
 */
function flyTo(latLng, zoom = null) {
  if (zoom === null) {
    map.flyTo(latLng);
  } else {
    map.flyTo(latLng, zoom);
  }
}

/**
 * Set the center point and optionally zoom level of the map
 *
 * @param {[number, number]} latLng [latitude, longitude]
 * @param {number} zoom Zoom level
 */
function goTo(latLng, zoom = null) {
  let options = { animate: false, duration: 0, noMoveStart: true };
  if (zoom === null) {
    map.setView(latLng, map.getZoom(), options);
  } else {
    map.setView(latLng, zoom, options);
  }
}

/**
 * Fits the map view to a set of bounds
 *
 * @param {L.LatLngBoundsExpression} bounds bounds to fit the map to
 * @param param1 {L.FitBoundsOptions}
 */
function fitBounds(bounds, { maxZoom = DEFAULT_POINT_ZOOM, ...options } = {}) {
  map.fitBounds(bounds, { maxZoom, ...options });
}

/**
 * Restricts the map view to not be able to be panned or zoomed outside of the given bounds
 *
 * @param {L.LatLngBounds} bounds bounds to restrict the map to
 * @param param1 {object}
 * @param param1.pad {number}
 */
function restrictToBounds(bounds, { pad } = { pad: null }) {
  fitBounds(bounds, { animate: false });
  let maxBounds = bounds;
  if (pad) maxBounds = maxBounds.pad(pad);
  map.setMaxBounds(maxBounds);

  return new Promise((resolve) => {
    setTimeout(() => {
      map.setMinZoom(map.getZoom());
      resolve();
    }, 0);
  });
}

/**
 * Pan and zoom so that the object given sits nicely in the map view.
 */
function goToObject(object) {
  let maxZoom = Math.min(map.getMaxZoom(), Math.max(DEFAULT_POINT_ZOOM, map.getZoom()));
  if (object.getBounds) {
    fitBounds(object.getBounds(), { maxZoom });
  } else if (object.getLatLng) {
    goToObject(object.getLatLng());
  } else {
    fitBounds([object, object], { maxZoom });
  }
}

function goToOperationBounds() {
  if (operationBounds) {
    goToObject(operationBounds);
  }
}

function enableCrosshair(lat, lng) {
  const latLngTest = !lat || !lng;

  if (!crosshair && latLngTest) {
    crosshair = new MapCrosshairLayer().addTo(map);
  }

  if (latLngTest) return;
  if (!vline || !hline) {
    vline = addPolyline([[-10000, lng], [10000, lng]], { color: 'white', weight: 1 });
    hline = addPolyline([[lat, -10000], [lat, 10000]], { color: 'white', weight: 1 });
  } else {
    vline.setLatLngs([[-10000, lng], [10000, lng]]);
    hline.setLatLngs([[lat, -10000], [lat, 10000]]);
  }
}

function disableCrosshair() {
  if (crosshair) {
    crosshair.remove();
    crosshair = null;
  }
  vline?.remove();
  hline?.remove();
  vline = null;
  hline = null;
}

function on(event, handler) {
  map.on(event, handler);
}

function getMap() {
  return map;
}

defineExpose({
  map,
  getMap,
  refresh,
  setCenter,
  getCenter,
  addMarker,
  showMarker,
  clearMarkers,
  startPoly,
  stopDrawing,
  isDrawing,
  addPolygon,
  loadOverlay,
  addOverlay,
  clearOverlays,
  removeObject,
  addPolyline,
  getBounds,
  flyToBounds,
  flyTo,
  goTo,
  fitBounds,
  restrictToBounds,
  goToObject,
  goToOperationBounds,
  enableCrosshair,
  disableCrosshair,
  on,
  resetZoom,
  diagramBoundsValidation,
});
</script>

<style lang="scss">
@import '@/stylesheets/components/maps/_map_container.scss';
</style>
